<script setup lang="ts">
import { isNil, isNumber } from 'lodash';
import { computed } from 'vue';

export type KeyValueListVariant = 'list' | 'list-stacked' | 'list-inline' | 'grid';
export type KeyValueListSize = 'regular' | 'small';

const {
  variant = 'list',
  size = 'regular',
  isDivided = false,
  labelWidth = null
} = defineProps<{
  variant?: KeyValueListVariant;
  size?: KeyValueListSize;
  isDivided?: boolean;
  labelWidth?: string | number | null;
}>();

const keyValueClasses = computed(() => ({
  '-list-stacked': variant === 'list' || variant === 'list-stacked',
  '-list-inline': variant === 'list-inline',
  '-grid': variant === 'grid',
  '-divided': isDivided,
  '-size-regular': size === 'regular',
  '-size-small': size === 'small'
}));

const keyValueStyles = computed(() => {
  if (isNil(labelWidth) || labelWidth === '') {
    return {};
  }

  return {
    '--key-value-label-width': isNumber(labelWidth) ? `${labelWidth}px` : labelWidth
  };
});
</script>

<template>
  <dl class="key-value-list" :class="keyValueClasses" :style="keyValueStyles">
    <slot></slot>
  </dl>
</template>

<style scoped lang="scss">
.key-value-list {
  --key-value-list-gap: #{$spacing-3};

  --key-value-label-font-size: 14px;
  --key-value-value-font-size: 16px;
  --key-value-direction: column;
  --key-value-gap: #{$spacing-2};
  --key-value-align-items: unset;

  display: flex;
  gap: var(--key-value-list-gap);
}

.-list-stacked {
  --key-value-list-gap: #{$spacing-5};

  flex-direction: column;
}

.-list-inline {
  --key-value-list-gap: #{$spacing-2};

  flex-direction: column;
}

/**
  * Divided styles: Apply ONLY when divided is used alongside list variants.
  */
.-divided.-list-stacked,
.-divided.-list-inline {
  > :not(:last-child, :only-child) {
    border-bottom: 1px solid $color-line-border;
    padding-bottom: var(--key-value-list-gap);
  }
}

.-list-inline {
  :deep(.key-value) {
    --key-value-direction: row;
    --key-value-align-items: center;
    --key-value-gap: #{$spacing-2};
  }

  :deep(.key-value dt) {
    width: var(--key-value-label-width, auto);
    flex: 0 0 var(--key-value-label-width, auto);
  }

  :deep(.key-value dd) {
    flex: 1 1 auto;
    min-width: 0;
  }
}

.-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr); // keeping this as a fallback for now
  grid-template-columns: repeat(auto-fit, minmax(30%, 150px));
  gap: $spacing-5;
  width: 100%;
}

.-size-small {
  --key-value-label-font-size: 12px; /* check with design */
  --key-value-value-font-size: 14px; /* check with design */
}

</style>
